/**
 * UX pass 2026-10-15 phase 2 (docs/design/ux-pass/README.md §4.1, P16): the structured name parts. A model that puts a
 * name in a sentence returns the name with a `Who` beside it (`NamePart`), so the view can link it with `WhoLink`
 * instead of baking it into a string. Pure; no React.
 */
import type { Actor } from "@minga/core"

export type Who =
  | { readonly kind: "agent"; readonly handle: string }
  | { readonly kind: "person"; readonly userId: string }

/** A name as it reads, and whose page it opens (null: Minga, a worker, a runner, someone who left). */
export interface NamePart {
  readonly name: string
  readonly who: Who | null
}

type Agents = { readonly agents: ReadonlyArray<{ readonly id: string; readonly handle: string }> }
type Members = { readonly members: ReadonlyArray<{ readonly userId: string }> }

/** A principal of the project (`{_tag: "agent", agentId}` / `{_tag: "user", userId}`) as a `Who`, by the project state. */
export const whoOfPrincipal = (
  state: Agents,
  p: { readonly _tag: string; readonly userId?: string | undefined; readonly agentId?: string | undefined },
): Who | null => {
  if (p._tag === "user" && p.userId) return { kind: "person", userId: p.userId }
  if (p._tag === "agent" && p.agentId) {
    const handle = state.agents.find((a) => a.id === p.agentId)?.handle
    return handle ? { kind: "agent", handle } : null
  }
  return null
}

/**
 * An actor of an event or record as a `Who`: a member opens their page, an agent hers. A worker (`W-3`), a runner and
 * Minga have no page; a person who left the workspace has none either (their page is archived).
 */
export const whoOfActor = (state: Agents & Members, a: Actor): Who | null => {
  if (a._tag === "user")
    return state.members.some((m) => m.userId === a.userId) ? { kind: "person", userId: a.userId } : null
  if (a._tag === "agent") return whoOfPrincipal(state, a)
  return null
}

/**
 * An actor as a name part, in the one wording for names (glossary, UX pass phase 2): a member by their name, an agent
 * by her name (not `@handle`), a worker by its agent's name, else "Minga" or "a runner".
 */
export const actorPart = (
  state: {
    readonly agents: ReadonlyArray<{ readonly id: string; readonly handle: string; readonly name: string }>
    readonly members: ReadonlyArray<{ readonly userId: string; readonly displayName: string }>
  },
  a: Actor,
): NamePart => {
  switch (a._tag) {
    case "user": {
      const member = state.members.find((m) => m.userId === a.userId)
      return member
        ? { name: member.displayName, who: { kind: "person", userId: a.userId } }
        : { name: "someone", who: null }
    }
    case "agent":
    case "worker": {
      const agent = state.agents.find((x) => x.id === a.agentId)
      if (!agent) return { name: "an agent", who: null }
      return { name: agent.name || `@${agent.handle}`, who: { kind: "agent", handle: agent.handle } }
    }
    case "runner":
      return { name: "a runner", who: null }
    case "system":
      return { name: "Minga", who: null }
  }
}

/** The agent that runs a run, as a `Who` (a lead or role-agent turn); null for a worker or validator run (`W-3`). */
export const whoOfRun = (
  state: Agents & {
    readonly runs: ReadonlyArray<{
      readonly id: string
      readonly kind: string
      readonly agentId?: string | null
    }>
  },
  runId: string,
): Who | null => {
  const run = state.runs.find((r) => r.id === runId)
  if (!run || run.kind === "worker" || run.kind === "validator" || !run.agentId) return null
  return whoOfPrincipal(state, { _tag: "agent", agentId: run.agentId })
}
